/**
 * Where to send a person after they sign in: the KrewUp page they were trying to open (`/login?next=/drive/document/<id>`).
 * Only a path INSIDE this app is ever returned -- never another domain, a protocol-relative address ("//evil.com"), a backslash
 * trick ("/\evil.com"), a scheme ("javascript:") or anything with control characters -- so a crafted link cannot turn the login
 * page into an open redirect. Anything else is null and the caller goes to the home page.
 */
const MAX_LENGTH = 2048;

export function safeReturnPath(raw: string | null | undefined): string | null {
  if (typeof raw !== 'string' || raw.length === 0 || raw.length > MAX_LENGTH) return null;
  if (!raw.startsWith('/') || raw.startsWith('//') || raw.includes('\\')) return null;
  // eslint-disable-next-line no-control-regex
  if (/[\u0000-\u001f\u007f]/.test(raw)) return null;
  let decoded: string;
  try {
    decoded = decodeURIComponent(raw);
  } catch {
    return null;
  }
  if (decoded.startsWith('//') || decoded.includes('\\') || /^\/+[a-z][a-z0-9+.-]*:/i.test(decoded)) return null;
  try {
    const url = new URL(raw, 'https://app.invalid');
    if (url.origin !== 'https://app.invalid') return null;
    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return null;
  }
}

/** The login address that comes back to `returnTo` afterwards (just `/login` when it is not a safe in-app path). */
export function loginPathWithNext(returnTo: string | null | undefined): string {
  const safe = safeReturnPath(returnTo);
  return safe && !safe.startsWith('/login') && !safe.startsWith('/auth/login') ? `/login?next=${encodeURIComponent(safe)}` : '/login';
}

/** The destination after a successful sign-in: the `next` of the current address when it is safe, otherwise `fallback`. */
export function destinationAfterLogin(search: string = window.location.search, fallback = '/'): string {
  return safeReturnPath(new URLSearchParams(search).get('next')) ?? fallback;
}

// -- loop guard -------------------------------------------------------------------------------------------------------------------------------------------------
// Login forwards a person who already has a live session straight to `next`. If that page sends them back to Login right away (the backend could not check the session, a
// route that bounces ...), forwarding again would loop for ever. So Login remembers where it forwarded to, for a few seconds: coming back to the same place within that window
// means "show the form", not "forward again".
const FORWARD_KEY = 'krewup:login-forward';
const FORWARD_WINDOW_MS = 15000;

export function recentlyForwardedTo(destination: string, now: number = Date.now()): boolean {
  try {
    const saved = JSON.parse(sessionStorage.getItem(FORWARD_KEY) || 'null');
    return !!saved && saved.to === destination && typeof saved.at === 'number' && now - saved.at < FORWARD_WINDOW_MS;
  } catch {
    return false;
  }
}

export function rememberForwardTo(destination: string, now: number = Date.now()): void {
  try {
    sessionStorage.setItem(FORWARD_KEY, JSON.stringify({ to: destination, at: now }));
  } catch {
    // storage unavailable: no guard, but nothing breaks
  }
}

export function clearForwardMemory(): void {
  try {
    sessionStorage.removeItem(FORWARD_KEY);
  } catch {
    // nothing to clear
  }
}
